import type {
  EmissionReportImport,
  EmissionReportReportRef,
} from '../../types/entities';

/**
 * Was eine Zeile der Importliste anzeigt und anbietet -- als reine Funktionen, damit die
 * Regeln D-13, D-14 und D-15 pruefbar sind und die Zeile selbst nichts entscheidet.
 *
 * Kein React-, Router- oder Mantine-Bezug: das Modul laeuft unter `environment: 'node'`.
 */

/** Zusatz hinter einem eingefrorenen Namen, dessen Verweis nicht mehr steht (D-13). */
const DELETED_SUFFIX = ' (gelöscht)';

/** D-14, offener Eintrag: die Meldung ist weg, die Kaskade bietet neu an. */
export const REPORT_DELETED_HINT =
  'Die zugeordnete Meldung wurde gelöscht — bitte neu zuordnen.';

/**
 * D-13 fuer einen einzelnen Teil der Zuordnung: Steht der Verweis, gilt der LEBENDE Name (damit
 * ein umbenannter Ofen richtig heisst und der Sprung ins Formular funktioniert). Ist der Verweis
 * weg und ein eingefrorener Name vorhanden, gilt dieser mit dem Zusatz „gelöscht". Sonst nichts.
 *
 * Der lebende Name steht im eingebetteten Verweis selbst -- die Seite loest ihn nicht mehr ueber
 * die Stammdaten nach. Damit gibt es auch kein Zeitfenster mehr, in dem die Zeile den
 * eingefrorenen Namen zeigt, weil die Stammdaten noch laden.
 */
function assignmentPart(
  reference: { name: string } | null | undefined,
  frozen: string | null
): string | null {
  if (reference != null) {
    return reference.name;
  }
  return frozen === null ? null : `${frozen}${DELETED_SUFFIX}`;
}

/**
 * Dasselbe fuer die Meldung, die keinen Namen traegt, sondern eine Periode -- angezeigt wird ihr
 * Jahrgang. Fehlt die Periode am lebenden Verweis, traegt der eingefrorene Text.
 */
function reportPart(
  reference: EmissionReportReportRef | null | undefined,
  frozen: string | null
): string | null {
  if (reference != null) {
    return formatReportingPeriod(reference.reportingPeriod) ?? frozen;
  }
  return frozen === null ? null : `${frozen}${DELETED_SUFFIX}`;
}

/**
 * `reportingPeriodAtImport` ist serverseitig ein Datum (Periodenanfang) und kommt als ISO-String
 * an. Umweltmeldungen sind jaehrlich, die Anzeige traegt also das Jahr. Rein ueber Zeichenketten
 * geschnitten und NICHT ueber `new Date(iso)`: das ergibt Mitternacht UTC und zeigt in einer Zone
 * mit negativem Offset den Vortag -- bei einem Jahreswechsel also das falsche Jahr.
 */
export function formatReportingPeriod(iso: string | null): string | null {
  return iso ? `Umweltdaten ${iso.slice(0, 4)}` : null;
}

/**
 * Die Zuordnung als „Werk · Ofen · Meldung" (D-13). `null` heisst: nichts zugeordnet, die Zeile
 * ist noch zuzuordnen.
 */
export function assignmentLabel(entry: EmissionReportImport): string | null {
  const parts = [
    assignmentPart(entry.plant, entry.plantNameAtImport),
    assignmentPart(entry.plantKiln, entry.kilnNameAtImport),
    reportPart(entry.environmentalData, formatReportingPeriod(entry.reportingPeriodAtImport)),
  ];

  const present: string[] = [];
  for (const part of parts) {
    if (part !== null) {
      present.push(part);
    }
  }

  return present.length === 0 ? null : present.join(' · ');
}

/**
 * Wie `assignmentLabel`, nur als DREI getrennte Werte statt einer verketteten Zeile -- der
 * Seitenkopf der Vorschau/des Protokolls (Plan 35-11) zeigt Werk/Ofen/Meldung als eigene
 * beschriftete Felder (Sketch 020, `.vhead .l2`). Dieselbe D-13-Regel, nur nicht zusammengefasst;
 * additiv zu `assignmentLabel`, das fuer die Listenzeile unveraendert bleibt.
 */
export function assignmentParts(entry: EmissionReportImport): {
  plant: string | null;
  kiln: string | null;
  period: string | null;
} {
  return {
    plant: assignmentPart(entry.plant, entry.plantNameAtImport),
    kiln: assignmentPart(entry.plantKiln, entry.kilnNameAtImport),
    period: reportPart(
      entry.environmentalData,
      formatReportingPeriod(entry.reportingPeriodAtImport)
    ),
  };
}

/** Vollstaendig zugeordnet heisst: alle drei Verweise stehen. */
export function isFullyAssigned(entry: EmissionReportImport): boolean {
  return entry.plant != null && entry.plantKiln != null && entry.environmentalData != null;
}

export type EmissionReportRowActionKey = 'preview' | 'log' | 'openReport' | 'discard';

export interface EmissionReportRowAction {
  key: EmissionReportRowActionKey;
  label: string;
  /** Ziel der Navigation; `null` bei einer Aktion, die die Zeile selbst ausfuehrt. */
  target: string | null;
  disabled: boolean;
  /** Warum gesperrt -- als Titel am Knopf, damit die Sperre erklaert ist statt nur zu wirken. */
  disabledReason?: string;
}

function detailRoute(entry: EmissionReportImport): string {
  // D-07: Vorschau und Protokoll sind dieselbe Route; nur die Beschriftung wechselt.
  return `/emission-reports/${entry.id}`;
}

/**
 * Die verfuegbaren Aktionen je Stand.
 *
 * - `open`: „Vorschau" (bei unvollstaendiger Zuordnung gesperrt) und „Verwerfen".
 * - `unreadable`: „Protokoll" und „Verwerfen" -- der Eintrag ist loeschbar (D-09b).
 * - `accepted`: „Protokoll", dazu „Zur Meldung", solange die Meldung existiert (D-14).
 *   KEIN „Verwerfen" (D-15) -- ab dem Uebernehmen ist der Eintrag unveraenderliche Historie.
 *   Die Regel erzwingt das Backend; das Ausblenden hier ist nur die Bequemlichkeit.
 */
export function rowActions(entry: EmissionReportImport): EmissionReportRowAction[] {
  if (entry.status === 'accepted') {
    const actions: EmissionReportRowAction[] = [
      { key: 'log', label: 'Protokoll', target: detailRoute(entry), disabled: false },
    ];
    if (entry.environmentalData != null) {
      actions.push({
        key: 'openReport',
        label: 'Zur Meldung',
        target: `/reports/environmental/${entry.environmentalData.id}`,
        disabled: false,
      });
    }
    return actions;
  }

  if (entry.status === 'unreadable') {
    return [
      { key: 'log', label: 'Protokoll', target: detailRoute(entry), disabled: false },
      { key: 'discard', label: 'Verwerfen', target: null, disabled: false },
    ];
  }

  const complete = isFullyAssigned(entry);
  return [
    {
      key: 'preview',
      label: 'Vorschau',
      target: detailRoute(entry),
      disabled: !complete,
      ...(complete ? {} : { disabledReason: 'Erst zuordnen: Werk, Ofen und Meldung.' }),
    },
    { key: 'discard', label: 'Verwerfen', target: null, disabled: false },
  ];
}

/**
 * Der Zusatztext der Zeile. Leerer String heisst: kein Hinweis.
 *
 * Der D-14-Satz haengt an „Werk und Ofen stehen, die Meldung nicht": ein frisch abgelegter
 * Eintrag hat gar keine Zuordnung und soll nicht behaupten, ihm sei etwas geloescht worden.
 * Offen bleibt eine Zweideutigkeit, die die Daten nicht aufloesen koennen -- dieselbe Lage
 * entsteht mitten in der Kaskade, wenn Werk und Ofen gewaehlt sind und die Meldung noch nicht.
 * Sobald die Kaskade da ist (Plan 35-10), unterdrueckt sie den Hinweis fuer die Zeile, an der
 * gerade gearbeitet wird.
 */
export function statusHint(entry: EmissionReportImport): string {
  if (entry.status === 'unreadable') {
    return 'Nicht lesbar — Protokoll ansehen';
  }

  if (entry.status === 'accepted') {
    return entry.environmentalData == null ? 'Meldung gelöscht' : '';
  }

  if (entry.environmentalData == null && entry.plant != null && entry.plantKiln != null) {
    return REPORT_DELETED_HINT;
  }

  return '';
}
